import { css } from '@emotion/react';
import {
  type FC, useEffect, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import type { AccordionData } from '~/_shared/baseComponents/accordion';
import { CheckboxGroupComponent } from '~/_shared/baseComponents/checkbox/checkboxGroup.component';
import {
  ExportDataModalComponent, type ExportDataModalResults,
} from '~/_shared/components/exportDataModal/exportDataModal.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { ProximityType } from '~/_shared/types/proximity/proximity.enums';
import { type SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { createUuid } from '~/_shared/utils/createUuid';
import { useTranslation } from '~/_shared/utils/hooks';
import { AppErrorType } from '~/appError/appErrorType.enum';
import { ExportDataFileType } from '~/data/exportDataModal/exportDataFileType.enum';
import { ExportDataMethod } from '~/data/exportDataModal/exportDataMethod.enum';
import { useFilteredLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';
import { type ModalProps } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { useProximities } from '~/proximity/useProximities';
import { ExportProximityDistanceCalculateMethodSectionComponent } from '~/proximityDetails/export/exportProximityDistanceCalculateMothodSection.component';
import { ExportProximityLimitResultsSectionComponent } from '~/proximityDetails/export/exportProximityLimitResultsSection.component';
import {
  ProximityDistanceDisabledReason, useExportProximityDistanceCalculate,
} from '~/proximityDetails/export/useExportProximityDistanceCalculate';
import {
  ProximityLimitMarkersType, useExportProximityLimitResults,
} from '~/proximityDetails/export/useExportProximityLimitResults';
import { proximityExportCreate } from '~/store/frontendState/proximityTool/proximityTool.actionCreators';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';
import { useSpreadsheetDataDataSelector } from '~/store/spreadsheetData/spreadsheetData.selectors';
import { getRequestsFromProximity } from './exportProximityData.helpers';

export type ExportProximityDataContainerProps = ModalProps<{
  selectedProximityIds: ReadonlyArray<string>;
  spreadsheetRowId?: SpreadsheetRowId;
  showSelectProximitiesSection: boolean;
}>;

const limitResultsSectionStyle = css({
  margin: -14,
});

const radiusLimitExceededStyle = (theme: Theme) => css({
  margin: '10px 0 0 0',
  color: theme.textColors.danger,
});

const noteAboutLimitStyle = (theme: Theme) => css({
  margin: '10px 10px 0 0',
  color: theme.textColors.info,
});

const PROXIMITY_EXPORT_CIRCLES_PLUS_POLYGONS_LIMIT = 250;

export const ExportProximityDataContainer: FC<ExportProximityDataContainerProps> = (props) => {
  const [selectedProximityIds, setSelectedProximityIds] = useState(props.selectedProximityIds);
  const [isSelectionSectionExpanded, setIsSelectionSectionExpanded] = useState(true);
  const [isDistanceCalculationMethodExpanded, setIsDistanceCalculationMethodExpanded] = useState(true);
  const [isLimitResultsSectionExpanded, setIsLimitResultsSectionExpanded] = useState(true);
  const [selectedRadiusAndDtpsCount, setSelectedRadiusAndDtpsCount] = useState(0);
  const isRadiusCountLimitExceeded = selectedRadiusAndDtpsCount > PROXIMITY_EXPORT_CIRCLES_PLUS_POLYGONS_LIMIT;

  const [t] = useTranslation();
  const dispatch = useDispatch();
  const virtualSpreadsheetId = usePrimarySpreadsheetId();
  const proximities = useProximities();
  const spreadsheetData = useSpreadsheetDataDataSelector();
  const visibleSpreadsheetLatLngs = useFilteredLatLngSpreadsheetData().visible;
  const exportLimits = useExportProximityLimitResults();
  const { replaceCurrentModal } = useModal('AppError');

  const selectedProximities = proximities
    .filter(proximity => selectedProximityIds.includes(proximity.id));
  const selectedSpreadsheetRowId = props.spreadsheetRowId ?? null;

  const distanceCalculate = useExportProximityDistanceCalculate(
    selectedRadiusAndDtpsCount,
    exportLimits.markersLimitType === ProximityLimitMarkersType.ClosestToCenter ? exportLimits.markersCount : null
  );

  const isAnySelectedProximityRadius = selectedProximities.filter(proximity => {
    return proximity.type === ProximityType.DistanceRadius;
  }).length > 0;

  const onSave = (arg: ExportDataModalResults) => {
    if (!virtualSpreadsheetId || selectedProximityIds.length === 0) {
      return;
    }

    dispatch(proximityExportCreate(createUuid(), {
      spreadsheetId: virtualSpreadsheetId,
      proximityIds: selectedProximityIds,
      selectedSpreadsheetRowId,
      exportAsText: arg.method === ExportDataMethod.Clipboard,
      extension: arg.method === ExportDataMethod.Clipboard ? ExportDataFileType.Csv : arg.fileType,
      exportLimits,
      distanceCalculate,
    }));

    props.onClose();

    return Promise.resolve();
  };

  useEffect(() => {
    if (!spreadsheetData) {
      return;
    }

    const radiusCount = proximities.reduce<number>((acc, proximity) => {
      if (!selectedProximityIds.includes(proximity.id)) {
        return acc;
      }

      const requests = getRequestsFromProximity(
        proximity,
        visibleSpreadsheetLatLngs, // the export looks at filtered group circles only
        spreadsheetData,
        selectedSpreadsheetRowId,
      );

      return acc + requests.circles.length + requests.polygons.length;
    }, 0);

    setSelectedRadiusAndDtpsCount(radiusCount);

    // show modal if radius proximity exceeds the circles limit instead of a warning
    if (radiusCount > PROXIMITY_EXPORT_CIRCLES_PLUS_POLYGONS_LIMIT && !props.showSelectProximitiesSection) {
      replaceCurrentModal({ appError: {
        type: AppErrorType.General,
        title: t('Error'),
        content: t('proximityExport.overCirclesLimitError', { limit: PROXIMITY_EXPORT_CIRCLES_PLUS_POLYGONS_LIMIT, current: radiusCount }),
      } });
    }
  }, [selectedProximityIds, props.selectedProximityIds, proximities, spreadsheetData, props.showSelectProximitiesSection, replaceCurrentModal, t, visibleSpreadsheetLatLngs, selectedSpreadsheetRowId]);

  const sectionsAfterExportMethod: AccordionData[] = [{
    header: t('Limit Results'),
    isExpanded: isLimitResultsSectionExpanded,
    onHeadingClick: () => setIsLimitResultsSectionExpanded(isExpanded => !isExpanded),
    child: (
      <div css={limitResultsSectionStyle}>
        <ExportProximityLimitResultsSectionComponent {...exportLimits} showMarkersLimitOptions={isAnySelectedProximityRadius} />
      </div>
    ),
  }];

  if (selectedRadiusAndDtpsCount > 0 && distanceCalculate.drivingDisabledReason !== ProximityDistanceDisabledReason.PresentationalMap) {
    sectionsAfterExportMethod.push({
      header: t('Distance Calculation Method'),
      isExpanded: isDistanceCalculationMethodExpanded,
      onHeadingClick: () => setIsDistanceCalculationMethodExpanded(isExpanded => !isExpanded),
      child: (
        <div css={limitResultsSectionStyle}>
          <ExportProximityDistanceCalculateMethodSectionComponent {...distanceCalculate} />
        </div>
      ),
    });
  }

  const sectionsBeforeExportMethod = !props.showSelectProximitiesSection ? [] : [{
    header: t('Choose the proximities you want to export'),
    isExpanded: isSelectionSectionExpanded,
    onHeadingClick: () => setIsSelectionSectionExpanded(!isSelectionSectionExpanded),
    child: (
      <div>
        <CheckboxGroupComponent
          onSelectionChanged={setSelectedProximityIds}
          selectedValues={selectedProximityIds}
          items={proximities.map(proximity => ({
            value: proximity.id,
            text: proximity.name,
          }))}
        />
        {isRadiusCountLimitExceeded && (
          <>
            <p css={radiusLimitExceededStyle}>
              {t('proximityExport.overCirclesLimitError', { limit: PROXIMITY_EXPORT_CIRCLES_PLUS_POLYGONS_LIMIT, current: selectedRadiusAndDtpsCount })}
            </p>
            <p css={noteAboutLimitStyle}>
              {t('Note:')} {t('You can use the filter tool to limit circles on the map and export only a specific area.')}
            </p>
          </>
        )}
      </div>
    ),
  }];

  return (
    <ExportDataModalComponent
      sectionsAfterExportMethod={sectionsAfterExportMethod}
      sectionsBeforeExportMethod={sectionsBeforeExportMethod}
      onSubmit={onSave}
      onClose={props.onClose}
      isOpen={props.isOpen}
      isSubmitDisabled={selectedProximityIds.length === 0 || isRadiusCountLimitExceeded || !distanceCalculate.isValid}
      noticeTrialUsers={false} // this notice is resolved in the proximity export flow
      showCount={false}
    />
  );
};
